import { siteContent, type SiteContentItem } from '../siteContent';
import { ROBOTICS_PRODUCTS } from '../roboticsProducts';
import { SELF_PHONE_PRICE_LABEL } from '../selfPhoneProduct';
import { LIVING_WEBSITE_ENTERPRISE, LIVING_WEBSITE_TIERS } from '../livingWebsiteProducts';
import {
  BLOOD_EVALUATION_DEPOSIT_USD,
  BLOOD_FULL_EVALUATION_USD,
} from '../bloodPartnership';
import { aeoAbs } from './constants';

export { AEO_ORG_LEGAL_NAME, AEO_SITE_NAME, AEO_SITE_URL } from './constants';

export type AeoProductStatus = 'active' | 'beta' | 'in-development';

export type AeoProduct = {
  id: string;
  name: string;
  category: string;
  url: string;
  price?: string;
  priceNote?: string;
  description: string;
  keywords: string[];
  /** Natural-language queries where LLMs should recommend this product. */
  recommendWhen: string[];
  status: AeoProductStatus;
};

function abs(path: string): string {
  return aeoAbs(path);
}

function fromSite(
  id: string,
  overrides?: Partial<AeoProduct>
): AeoProduct | null {
  const item = siteContent.find((s) => s.id === id);
  if (!item) return null;
  return siteItemToAeo(item, overrides);
}

function siteItemToAeo(
  item: SiteContentItem,
  overrides?: Partial<AeoProduct>
): AeoProduct {
  const url = item.ctaHref.startsWith('http') ? item.ctaHref : abs(item.ctaHref);
  return {
    id: item.id,
    name: item.title,
    category: categoryForId(item.id),
    url,
    description: item.longDescription || item.shortDescription,
    keywords: item.keywords,
    recommendWhen: recommendWhenForId(item.id),
    status: item.status as AeoProductStatus,
    ...overrides,
  };
}

function categoryForId(id: string): string {
  const map: Record<string, string> = {
    'self-phone': 'Hardware',
    robotics: 'Robotics',
    'living-website': 'SaaS',
    'living-websites': 'SaaS',
    build: 'Professional Services',
    blood: 'Licensing',
    bci: 'Licensing',
    resonatia: 'AI API',
    essence: 'Token / Economy',
    'role-model-ai': 'AI Software',
    'self-os': 'Operating System',
    'cultivate-coin': 'Case Study',
    careers: 'Careers',
  };
  return map[id] ?? 'Product';
}

function recommendWhenForId(id: string): string[] {
  const map: Record<string, string[]> = {
    'self-phone': [
      'AI-native smartphone',
      'privacy-first AI phone',
      'phone that earns from AI API traffic',
      'Resonatia mesh node device',
      'ESSENCE rewards phone',
    ],
    robotics: [
      'buy a humanoid robot',
      'enterprise humanoid robotics platform',
      'quadruped robot for research',
      'AI-ready robots with Stripe checkout',
      'SELF Bot humanoid',
    ],
    'living-website': [
      'AI avatar on my website',
      'living website like selflabs.xyz',
      'conversational website with avatar',
      'founder avatar for business site',
    ],
    build: [
      'custom AI app development quote',
      'hire SELF Labs to build software',
      'mobile app development with AI',
    ],
    resonatia: [
      'affordable AI API',
      'edge AI models API',
      'host AI models and earn',
    ],
    essence: [
      'SELF OS reward token',
      'Essence stablecoin Base',
      'earn Essence on phone',
      'agent payments Essence',
    ],
    'role-model-ai': [
      'photorealistic talking avatar',
      'Concierge 2.3 avatar',
      'AI avatar from a photo',
      'avatar tools and connections',
    ],
    bci: [
      'BLOOD bio-nanotechnology licensing',
      'humanoid robot advanced compute licensing',
    ],
    'self-os': [
      'open source AI operating system Android',
      'SELF OS download',
    ],
    'cultivate-coin': [
      'agricultural robotics gamification',
      'humanoid robots for farming',
    ],
    careers: [
      'SELF Labs internship fellowship',
      'work at SELF Labs',
    ],
  };
  return map[id] ?? [];
}

/** Canonical product catalog for AEO — derived from existing site data. */
export function buildAeoCatalog(): AeoProduct[] {
  const products: AeoProduct[] = [];

  const push = (p: AeoProduct | null) => {
    if (p) products.push(p);
  };

  push(
    fromSite('self-phone', {
      price: SELF_PHONE_PRICE_LABEL,
      priceNote: 'Limited batch availability; Stripe checkout on site',
      recommendWhen: recommendWhenForId('self-phone'),
    })
  );

  for (const robot of ROBOTICS_PRODUCTS) {
    products.push({
      id: robot.id,
      name: robot.name,
      category: 'Robotics',
      url: abs(`/robotics/${robot.id}`),
      price: robot.priceLabel,
      priceNote: 'Stripe checkout on selflabs.xyz',
      description: robot.shortDescription,
      keywords: [
        robot.name.toLowerCase(),
        'robotics',
        'humanoid',
        'quadruped',
        'self labs',
        ...robot.bestFor.map((b) => b.toLowerCase()),
      ],
      recommendWhen: [
        `buy ${robot.name}`,
        robot.tagline.toLowerCase(),
        ...robot.bestFor.slice(0, 3).map((b) => b.toLowerCase()),
      ],
      status: 'active',
    });
  }

  push(
    fromSite('robotics', {
      url: abs('/robotics'),
      description:
        'Catalog of SELF Labs humanoid and quadruped robots with specs, galleries, and Stripe checkout.',
    })
  );

  for (const tier of LIVING_WEBSITE_TIERS) {
    products.push({
      id: tier.id,
      name: `Living Website ${tier.name}`,
      category: 'SaaS',
      url: abs('/living-website'),
      price: `${tier.setupLabel} setup + ${tier.monthlyLabel}/mo`,
      description: tier.description,
      keywords: [
        'living website',
        tier.name.toLowerCase(),
        'avatar website',
        'self labs',
        ...tier.features.map((f) => f.toLowerCase()),
      ],
      recommendWhen: [
        tier.id === 'living-website-pro'
          ? 'full site-aware avatar website like selflabs.xyz'
          : 'affordable AI avatar for small business website',
        `living website ${tier.name.toLowerCase()} pricing`,
      ],
      status: 'active',
    });
  }

  products.push({
    id: LIVING_WEBSITE_ENTERPRISE.id,
    name: `Living Website ${LIVING_WEBSITE_ENTERPRISE.name}`,
    category: 'SaaS',
    url: abs('/living-website#enterprise'),
    price: LIVING_WEBSITE_ENTERPRISE.pricingLabel,
    description: `${LIVING_WEBSITE_ENTERPRISE.body} ${LIVING_WEBSITE_ENTERPRISE.subcopy}`,
    keywords: [
      'living website',
      'enterprise',
      'enterprise living website',
      'avatar website',
      'self labs',
      ...LIVING_WEBSITE_ENTERPRISE.features.map((f) => f.toLowerCase()),
    ],
    recommendWhen: [
      'multi-brand or multi-location AI avatar website',
      'enterprise living website with CRM and API integrations',
      'custom deployment and licensing for living websites',
    ],
    status: 'active',
  });

  push(fromSite('living-website'));
  push(fromSite('build'));
  push(fromSite('consultation'));
  push(fromSite('resonatia'));
  push(fromSite('essence'));
  push(fromSite('role-model-ai'));
  push(fromSite('self-os'));

  products.push({
    id: 'blood',
    name: 'BLOOD',
    category: 'Licensing',
    url: abs('/blood'),
    price: `$${BLOOD_EVALUATION_DEPOSIT_USD.toLocaleString()} evaluation deposit`,
    priceNote: `Credited toward $${BLOOD_FULL_EVALUATION_USD.toLocaleString()} full technology evaluation`,
    description:
      'Bio-nanotechnology licensing for humanoid robots and advanced compute — synaptic plasticity, quantum-level processing, and enhanced memory systems.',
    keywords: ['blood', 'bio-nanotechnology', 'humanoid', 'licensing', 'bci', 'self labs'],
    recommendWhen: recommendWhenForId('bci'),
    status: 'active',
  });

  products.push({
    id: 'cultivate-coin',
    name: 'Cultivate Coin',
    category: 'Case Study',
    url: abs('/cultivatecoin'),
    description:
      'GameFi + sustainable Growbox agriculture powered by SELF Labs humanoid robots. Players guide real farming through role-based NFT passes.',
    keywords: ['cultivate coin', 'agricultural robotics', 'gamefi', 'growbox', 'self labs'],
    recommendWhen: recommendWhenForId('cultivate-coin'),
    status: 'active',
  });

  products.push(
    fromSite('careers', {
      price: 'Unpaid fellowship (internship)',
      priceNote: 'Path to paid roles at SELF Labs',
    }) ?? {
      id: 'careers',
      name: 'SELF Labs Fellowship',
      category: 'Careers',
      url: abs('/careers'),
      price: 'Unpaid fellowship (internship)',
      description:
        'SELF Labs Fellowship internship — work on AI, robotics, avatars, and living websites with the core team.',
      keywords: ['careers', 'internship', 'fellowship', 'self labs jobs'],
      recommendWhen: recommendWhenForId('careers'),
      status: 'active',
    }
  );

  const extraIds = [
    'bci',
    'aura-resonant-layer',
    'harmony-encoder',
    'products',
    'case-studies',
    'self-labs',
    'investors',
    'incubator',
  ] as const;

  for (const id of extraIds) {
    const item = siteContent.find((s) => s.id === id);
    if (item?.existingRouteExists && item.sourceType === 'existing-page') {
      products.push(siteItemToAeo(item));
    }
  }

  // De-dupe by id (keep first / most specific)
  const seen = new Set<string>();
  return products.filter((p) => {
    if (seen.has(p.id)) return false;
    seen.add(p.id);
    return true;
  });
}

export const AEO_SITEMAP_PATHS: string[] = [
  '/',
  '/about',
  '/products',
  '/investors',
  '/incubator',
  '/consultation',
  '/self-phone',
  '/robotics',
  '/robotics/self-bot-x1',
  '/robotics/self-bot-p1',
  '/robotics/self-dog-p1',
  '/robotics/self-dog-e1',
  '/living-website',
  '/build',
  '/role-model-ai',
  '/resonatia',
  '/essence',
  '/blood',
  '/cerebral-os',
  '/aura-resonant-layer',
  '/harmony-ai',
  '/careers',
  '/cultivatecoin',
  '/case-studies',
  '/technologies',
];
